import * as i0 from '@angular/core';
import { Injectable, Input, Component, ViewEncapsulation, NgModule } from '@angular/core';
import { NgIf, NgClass } from '@angular/common';
import { animationFadeInOut } from '@engineering11/ui-lib/e11-animations';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

class E11BackdropService {
    // Observable string streams
    constructor() {
        // Observable sources
        this.backdropConfigSource = new Subject();
        this.backdropConfig$ = this.backdropConfigSource.asObservable();
    }
    backdropDisplay(status, style) {
        this.backdropConfigSource.next({ status, style: style ?? defaultBackdropConfig.style });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

const defaultBackdropConfig = {
    status: false,
    style: 'dark',
};
/**
 * A backdrop component that can be used to show a backdrop on the entire screen.
 * You can use e11-backdrop-global component to show a backdrop on the entire screen.
 *
 * This is an alternative to e11-backdrop-global component.
 */
class E11BackdropComponent {
    constructor(backdropService) {
        this.backdropService = backdropService;
        /**
         * The status of the backdrop.
         */
        this.status = defaultBackdropConfig.status;
        /**
         * The style of the backdrop.
         */
        this.style = defaultBackdropConfig.style;
        this.destroy$ = new Subject();
        this.backdropService.backdropConfig$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            if (data) {
                this.status = data.status;
                this.style = data.style ? data.style : defaultBackdropConfig.style;
            }
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropComponent, deps: [{ token: E11BackdropService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11BackdropComponent, isStandalone: true, selector: "e11-backdrop", inputs: { status: "status", style: "style" }, ngImport: i0, template: `
    <div
      *ngIf="status"
      class="e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm"
      [ngClass]="{ 'e11-bg-black/70': style === 'dark', 'e11-bg-white/70': style === 'light' }"
      [@animationFadeInOut]
    ></div>
  `, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], animations: [animationFadeInOut()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-backdrop', standalone: true, imports: [NgIf, NgClass], template: `
    <div
      *ngIf="status"
      class="e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm"
      [ngClass]="{ 'e11-bg-black/70': style === 'dark', 'e11-bg-white/70': style === 'light' }"
      [@animationFadeInOut]
    ></div>
  `, animations: [animationFadeInOut()] }]
        }], ctorParameters: () => [{ type: E11BackdropService }], propDecorators: { status: [{
                type: Input
            }], style: [{
                type: Input
            }] } });

class E11BackdropGlobalService {
    constructor() {
        this.config$ = new BehaviorSubject(null);
        this.backdropOpened$ = new Subject();
        this.backdropClosed$ = new Subject();
    }
    openBackdrop(config) {
        const configToUse = { ...defaultBackdropConfig, status: true, ...config };
        this.config$.next(configToUse);
        this.backdropOpened$.next(true);
    }
    closeBackdrop() {
        this.config$.next(null);
        this.backdropClosed$.next(true);
        this.currentModal = undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropGlobalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropGlobalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropGlobalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * A global backdrop component that can be used to show a backdrop on the entire screen.
 * Usually one instance of this component is enough for the entire application.
 */
class E11BackdropGlobalComponent {
    constructor(backdropGlobalService) {
        this.backdropGlobalService = backdropGlobalService;
        this.config = defaultBackdropConfig;
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.backdropGlobalService.config$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
            if (config) {
                this.config = config;
            }
            else {
                this.config = { ...defaultBackdropConfig, ...this.config, status: false };
            }
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropGlobalComponent, deps: [{ token: E11BackdropGlobalService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11BackdropGlobalComponent, isStandalone: true, selector: "e11-backdrop-global", ngImport: i0, template: ` <e11-backdrop *ngIf="config" [style]="config.style" [status]="config.status"></e11-backdrop> `, isInline: true, dependencies: [{ kind: "component", type: E11BackdropComponent, selector: "e11-backdrop", inputs: ["status", "style"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropGlobalComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-backdrop-global',
                    standalone: true,
                    imports: [E11BackdropComponent, NgIf],
                    template: ` <e11-backdrop *ngIf="config" [style]="config.style" [status]="config.status"></e11-backdrop> `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [{ type: E11BackdropGlobalService }] });

class E11BackdropModule {
    static forRoot() {
        return {
            ngModule: E11BackdropModule,
            providers: [],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropModule, imports: [E11BackdropGlobalComponent, E11BackdropComponent], exports: [E11BackdropComponent, E11BackdropGlobalComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BackdropModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11BackdropGlobalComponent, E11BackdropComponent],
                    exports: [E11BackdropComponent, E11BackdropGlobalComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11BackdropComponent, E11BackdropGlobalComponent, E11BackdropGlobalService, E11BackdropModule, E11BackdropService, defaultBackdropConfig };
